<script lang="ts">
  import { onMount } from 'svelte';
  import type {
    ConnectionSummary,
    DeviceGroupSummary,
    DeviceStatus,
    DeviceSummary,
    MqttChannelSummary,
    TenantSummary,
    UpstreamFormatSummary,
  } from '@ucc/shared';
  import {
    ApiError,
    assignDeviceGroup,
    deleteGroup,
    fetchConnections,
    fetchGroup,
    fetchMqttChannels,
    fetchTenants,
    fetchUpstreamFormats,
    updateGroup,
  } from '$lib/api';
  import { deviceHref } from '$lib/nav';
  import { session } from '$lib/session.svelte';
  import Badge from '$lib/components/Badge.svelte';
  import Button from '$lib/components/Button.svelte';
  import Card from '$lib/components/Card.svelte';

  let {
    groupId,
  }: {
    groupId: string;
  } = $props();

  let group = $state<DeviceGroupSummary | null>(null);
  let members = $state<DeviceSummary[]>([]);
  let available = $state<DeviceSummary[]>([]);
  let tenants = $state<TenantSummary[]>([]);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let actionError = $state<string | null>(null);
  let actionOk = $state<string | null>(null);
  let busyId = $state<string | null>(null);
  let pickId = $state('');
  let editName = $state('');
  let editDescription = $state('');
  let savingMeta = $state(false);
  let deleting = $state(false);

  let samplingFormats = $state<UpstreamFormatSummary[]>([]);
  let alarmFormats = $state<UpstreamFormatSummary[]>([]);
  let connections = $state<ConnectionSummary[]>([]);
  let mqttChannels = $state<MqttChannelSummary[]>([]);
  let pickSamplingId = $state('');
  let pickAlarmId = $state('');
  /** A group has one delivery target: 'https:<connectionId>' or 'mqtt:<channelId>', or '' for none. */
  let pickDeliveryTarget = $state('');
  let savingUpstream = $state(false);
  let upstreamError = $state<string | null>(null);
  let upstreamOk = $state<string | null>(null);

  const isAdmin = $derived(session.user?.role === 'admin');
  const isClient = $derived(session.user?.role === 'client');
  const isStaff = $derived(isAdmin || session.user?.role === 'user');
  /** Staff + clients may assign owned devices (API enforces ownership). */
  const canAssign = $derived(
    session.user?.role === 'admin' ||
      session.user?.role === 'user' ||
      session.user?.role === 'client',
  );
  /** Same-side group rename/delete (lock Q). */
  const canManage = $derived(
    session.user?.role === 'admin' ||
      session.user?.role === 'user' ||
      session.user?.role === 'client',
  );

  const tenantLabel = $derived.by(() => {
    if (!group) return '—';
    return tenants.find((t) => t.id === group!.tenantId)?.name ?? group.tenantId;
  });

  function statusTone(status: DeviceStatus): 'success' | 'neutral' | 'warning' | 'danger' | 'accent' {
    switch (status) {
      case 'online':
        return 'success';
      case 'offline':
        return 'neutral';
      case 'disabled':
        return 'danger';
      case 'logging':
        return 'accent';
      default:
        return 'neutral';
    }
  }

  /** UI label — never surface API status "unknown" as text. */
  function statusLabel(status: DeviceStatus): string {
    if (status === 'unknown') return '—';
    return status.charAt(0).toUpperCase() + status.slice(1);
  }

  async function load(id: string) {
    loading = true;
    error = null;
    actionError = null;
    group = null;
    members = [];
    available = [];
    try {
      const gRes = await fetchGroup(id);
      group = gRes.group;
      members = gRes.devices;
      available = gRes.available;
      editName = gRes.group.name ?? '';
      editDescription = gRes.group.description ?? '';
      pickId = available[0]?.id ?? '';
      if (!isClient) {
        const tRes = await fetchTenants().catch(() => ({ tenants: [] as TenantSummary[] }));
        tenants = tRes.tenants;
      } else {
        tenants = [];
        const [sRes, aRes, cRes, mRes] = await Promise.all([
          fetchUpstreamFormats('sampling').catch(() => ({ formats: [] as UpstreamFormatSummary[] })),
          fetchUpstreamFormats('alarm').catch(() => ({ formats: [] as UpstreamFormatSummary[] })),
          fetchConnections().catch(() => ({ connections: [] as ConnectionSummary[] })),
          fetchMqttChannels().catch(() => ({ channels: [] as MqttChannelSummary[] })),
        ]);
        samplingFormats = sRes.formats;
        alarmFormats = aRes.formats;
        connections = cRes.connections;
        mqttChannels = mRes.channels;
        pickSamplingId = gRes.group.samplingFormatId ?? '';
        pickAlarmId = gRes.group.alarmFormatId ?? '';
        pickDeliveryTarget = gRes.group.connectionId
          ? `https:${gRes.group.connectionId}`
          : gRes.group.mqttChannelId
            ? `mqtt:${gRes.group.mqttChannelId}`
            : '';
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        error = 'Group not found or not visible in your tenant.';
      } else if (err instanceof ApiError && err.status === 403) {
        error = 'You do not have access to groups.';
      } else {
        error = err instanceof ApiError ? err.message : 'Failed to load group';
      }
    } finally {
      loading = false;
    }
  }

  async function addDevice() {
    if (!pickId || !group) return;
    busyId = pickId;
    actionError = null;
    actionOk = null;
    try {
      await assignDeviceGroup(pickId, group.id);
      actionOk = 'Device added to group.';
      await load(groupId);
    } catch (err) {
      actionError = err instanceof ApiError ? err.message : 'Assign failed';
    } finally {
      busyId = null;
    }
  }

  async function removeDevice(deviceId: string) {
    busyId = deviceId;
    actionError = null;
    actionOk = null;
    try {
      await assignDeviceGroup(deviceId, null);
      actionOk = 'Device removed from group.';
      await load(groupId);
    } catch (err) {
      actionError = err instanceof ApiError ? err.message : 'Remove failed';
    } finally {
      busyId = null;
    }
  }


  async function saveMeta() {
    if (!canManage || !group) return;
    const name = editName.trim();
    if (!name) {
      actionError = 'Name is required.';
      return;
    }
    savingMeta = true;
    actionError = null;
    actionOk = null;
    try {
      const res = await updateGroup(group.id, {
        name,
        description: editDescription.trim() ? editDescription.trim() : null,
      });
      group = res.group;
      editName = res.group.name ?? '';
      editDescription = res.group.description ?? '';
      actionOk = 'Group updated.';
    } catch (err) {
      actionError = err instanceof ApiError ? err.message : 'Failed to update group';
    } finally {
      savingMeta = false;
    }
  }

  async function onSaveUpstream() {
    if (!isClient || !group) return;
    savingUpstream = true;
    upstreamError = null;
    upstreamOk = null;
    const [kind, targetId] = pickDeliveryTarget ? pickDeliveryTarget.split(':') : [null, null];
    try {
      const res = await updateGroup(group.id, {
        samplingFormatId: pickSamplingId || null,
        alarmFormatId: pickAlarmId || null,
        connectionId: kind === 'https' ? targetId! : null,
        mqttChannelId: kind === 'mqtt' ? targetId! : null,
      });
      group = res.group;
      upstreamOk = 'Upstream settings saved.';
    } catch (err) {
      upstreamError = err instanceof ApiError ? err.message : 'Failed to save upstream settings';
    } finally {
      savingUpstream = false;
    }
  }

  async function onDelete() {
    if (!canManage || !group || members.length > 0) return;
    const ok = confirm(`Delete group “${group.name}”? This cannot be undone.`);
    if (!ok) return;
    deleting = true;
    actionError = null;
    actionOk = null;
    try {
      await deleteGroup(group.id);
      location.hash = '#/groups';
    } catch (err) {
      actionError = err instanceof ApiError ? err.message : 'Failed to delete group';
      deleting = false;
    }
  }

  onMount(() => {
    void load(groupId);
  });
</script>

<div class="page">
  <header class="head">
    <div>
      <a class="back" href="#/groups">← Groups</a>
      {#if group}
        <h2>{group.name}</h2>
        <p class="sub">
          {group.description?.trim()
            ? group.description
            : isClient
              ? 'Your devices in this group'
              : 'Group membership'}
        </p>
      {:else if loading}
        <h2>Group</h2>
        <p class="sub muted">Loading…</p>
      {:else}
        <h2>Group</h2>
      {/if}
    </div>
    {#if group}
      <div class="head-meta">
        <span class="count">{group.deviceCount ?? members.length} devices</span>
        {#if isStaff}
          <span class="tenant">{tenantLabel}</span>
        {/if}
      </div>
    {/if}
  </header>

  {#if actionError}
    <p class="banner error" role="alert">{actionError}</p>
  {/if}
  {#if actionOk}
    <p class="banner ok" role="status">{actionOk}</p>
  {/if}

  {#if error}
    <Card>
      <p class="error">{error}</p>
    </Card>
  {:else if loading}
    <Card>
      <p class="muted">Loading…</p>
    </Card>
  {:else if group}
    {#if actionError}
      <p class="banner error" role="alert">{actionError}</p>
    {/if}

    <div class="grid">
      <Card
        title="Members"
        subtitle={isClient ? 'Your claimed devices in this group' : 'Devices currently in this group'}
        padded={false}
      >
        {#if members.length === 0}
          <p class="banner muted">No devices in this group yet.</p>
        {:else}
          <div class="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>UID</th>
                  {#if canAssign}<th class="actions"> </th>{/if}
                </tr>
              </thead>
              <tbody>
                {#each members as d (d.id)}
                  <tr>
                    <td>
                      <a class="name" href={deviceHref(d.id)}>{d.name}</a>
                    </td>
                    <td>
                      {#if d.status === 'unknown'}
                        <span class="muted-dash">—</span>
                      {:else}
                        <Badge tone={statusTone(d.status)}>{statusLabel(d.status)}</Badge>
                      {/if}
                    </td>
                    <td class="mono wrap">{d.uid}</td>
                    {#if canAssign}
                      <td class="actions">
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={busyId === d.id}
                          onclick={() => void removeDevice(d.id)}
                        >
                          {busyId === d.id ? '…' : 'Remove'}
                        </Button>
                      </td>
                    {/if}
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        {/if}
      </Card>

      <div class="side">
        {#if canAssign}
          <Card title="Add device" subtitle={isClient ? 'Your claimed devices not in this group' : 'Any device, not in this group'}>
            {#if available.length === 0}
              <p class="muted">
                {#if isClient}
                  No other claimed devices available to add.
                {:else}
                  No eligible devices outside this group.
                {/if}
              </p>
            {:else}
              <div class="assign">
                <label class="field grow">
                  <span class="sr-only">Device</span>
                  <select bind:value={pickId} disabled={busyId != null}>
                    {#each available as d (d.id)}
                      <option value={d.id}
                        >{d.name} · {d.uid.slice(0, 8)}…{#if !isClient}
                          · {d.tenantName ?? 'unclaimed'}{/if}</option
                      >
                    {/each}
                  </select>
                </label>
                <Button disabled={!pickId || busyId != null} onclick={() => void addDevice()}>
                  {busyId === pickId ? 'Adding…' : 'Add'}
                </Button>
              </div>
              <p class="hint">Assigning moves the device’s primary group membership.</p>
            {/if}
          </Card>
        {/if}

        <Card title="Details">
          <dl class="facts">
            {#if !canManage}
              <div>
                <dt>Name</dt>
                <dd>{group.name}</dd>
              </div>
            {/if}
            {#if isStaff}
              <div>
                <dt>Tenant</dt>
                <dd>{tenantLabel}</dd>
              </div>
            {/if}
            <div>
              <dt>Members</dt>
              <dd>{group.deviceCount ?? members.length}</dd>
            </div>
            <div>
              <dt>Id</dt>
              <dd class="mono wrap">{group.id}</dd>
            </div>
            {#if group.description && !canManage}
              <div>
                <dt>Description</dt>
                <dd>{group.description}</dd>
              </div>
            {/if}
          </dl>
        </Card>

        {#if isClient}
          <Card
            title="Upstream delivery"
            subtitle={group.samplingFormatId && group.alarmFormatId && (group.connectionId || group.mqttChannelId)
              ? `Delivering to ${group.connectionName ?? group.mqttChannelName ?? 'target'}`
              : 'Not delivering yet — set all three below'}
          >
            <div class="edit-form">
              <label class="field">
                <span>Sampling format</span>
                <select bind:value={pickSamplingId} disabled={savingUpstream}>
                  <option value="">— none —</option>
                  {#each samplingFormats as f (f.id)}
                    <option value={f.id}>{f.name}</option>
                  {/each}
                </select>
              </label>
              <label class="field">
                <span>Alarm format</span>
                <select bind:value={pickAlarmId} disabled={savingUpstream}>
                  <option value="">— none —</option>
                  {#each alarmFormats as f (f.id)}
                    <option value={f.id}>{f.name}</option>
                  {/each}
                </select>
              </label>
              <label class="field">
                <span>Connection</span>
                <select bind:value={pickDeliveryTarget} disabled={savingUpstream}>
                  <option value="">— none —</option>
                  {#each connections as c (c.id)}
                    <option value={`https:${c.id}`}>{c.name} (HTTPS)</option>
                  {/each}
                  {#each mqttChannels as c (c.id)}
                    <option value={`mqtt:${c.id}`}>{c.name} (MQTT)</option>
                  {/each}
                </select>
              </label>
              <div class="edit-actions">
                <Button variant="primary" disabled={savingUpstream} onclick={() => void onSaveUpstream()}>
                  {savingUpstream ? 'Saving…' : 'Save upstream settings'}
                </Button>
              </div>
              <p class="hint">
                Sampling and alarm formats must be built from the same profile. A device can only join
                this group if its active profile matches.
              </p>
              {#if upstreamError}
                <p class="error">{upstreamError}</p>
              {/if}
              {#if upstreamOk}
                <p class="ok-text">{upstreamOk}</p>
              {/if}
            </div>
          </Card>
        {/if}

        {#if canManage}
          <Card title="Edit group" subtitle="Rename or update description (unique name per tenant)">
            <div class="edit-form">
              <label class="field">
                <span>Name</span>
                <input
                  type="text"
                  bind:value={editName}
                  maxlength="120"
                  disabled={savingMeta || deleting}
                  required
                />
              </label>
              <label class="field">
                <span>Description</span>
                <textarea
                  rows="3"
                  bind:value={editDescription}
                  maxlength="500"
                  disabled={savingMeta || deleting}
                  placeholder="Optional notes"
                ></textarea>
              </label>
              <div class="edit-actions">
                <Button
                  variant="primary"
                  disabled={savingMeta || deleting || !editName.trim()}
                  onclick={() => void saveMeta()}
                >
                  {savingMeta ? 'Saving…' : 'Save'}
                </Button>
                <Button
                  variant="danger"
                  disabled={deleting || savingMeta || members.length > 0}
                  title={members.length > 0 ? 'Remove all devices before deleting' : 'Delete empty group'}
                  onclick={() => void onDelete()}
                >
                  {deleting ? 'Deleting…' : 'Delete group'}
                </Button>
              </div>
              {#if members.length > 0}
                <p class="hint">Delete is disabled while the group has members ({members.length}).</p>
              {/if}
            </div>
          </Card>
        {/if}
      </div>
    </div>
  {/if}
</div>

<style>
  .muted-dash {
    color: var(--muted, #6b7280);
  }

  .page {
    display: flex;
    flex-direction: column;
    gap: var(--space-5, 1.25rem);
  }

  .head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
  }

  .back {
    display: inline-block;
    margin-bottom: 0.4rem;
    font-size: var(--text-sm, 0.9rem);
    color: var(--text-muted);
    text-decoration: none;
  }

  .back:hover {
    color: var(--accent, #2563eb);
  }

  h2 {
    margin: 0;
    font-size: var(--text-xl, 1.35rem);
    font-weight: 650;
  }

  .sub {
    margin: 0.35rem 0 0;
    color: var(--text-muted);
    font-size: var(--text-sm, 0.9rem);
  }

  .head-meta {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.25rem;
    font-size: var(--text-sm, 0.9rem);
  }

  .count {
    font-weight: 650;
  }

  .tenant {
    color: var(--text-muted);
  }

  .grid {
    display: grid;
    grid-template-columns: 1.4fr 1fr;
    gap: 1rem;
  }

  .side {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .error {
    color: var(--danger, #b91c1c);
  }

  .ok-text {
    color: var(--success, #15803d);
    margin: 0;
    font-size: var(--text-sm, 0.9rem);
  }

  .muted {
    color: var(--text-muted);
    margin: 0;
  }

  .table-wrap {
    overflow-x: auto;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: var(--text-sm, 0.9rem);
  }

  th,
  td {
    text-align: left;
    padding: 0.65rem 1rem;
    border-bottom: 1px solid var(--border);
    white-space: nowrap;
  }

  th {
    font-size: var(--text-xs, 0.75rem);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--text-muted);
    font-weight: 600;
    background: var(--surface-2, #f9fafb);
  }

  .name {
    color: inherit;
    font-weight: 600;
    text-decoration: none;
  }

  .name:hover {
    color: var(--accent, #2563eb);
    text-decoration: underline;
  }

  .mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 0.85em;
  }

  .wrap {
    word-break: break-all;
    white-space: normal;
  }

  .actions {
    text-align: right;
  }

  .assign {
    display: flex;
    flex-wrap: wrap;
    gap: 0.65rem;
    align-items: center;
  }

  .field.grow {
    flex: 1 1 12rem;
    min-width: 10rem;
  }

  select {
    width: 100%;
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 0.5rem 0.65rem;
    font: inherit;
    background: var(--surface);
    color: inherit;
  }

  .hint {
    margin: 0.75rem 0 0;
    font-size: var(--text-xs, 0.75rem);
    color: var(--text-muted);
  }

  .facts {
    margin: 0;
    display: grid;
    gap: 0.85rem;
  }

  .facts dt {
    font-size: var(--text-xs, 0.75rem);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--text-muted);
    margin-bottom: 0.2rem;
  }

  .facts dd {
    margin: 0;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    border: 0;
  }

  .banner {
    margin: 0;
    padding: 0.65rem 0.85rem;
    border-radius: 8px;
    font-size: var(--text-sm);
  }

  .banner.ok {
    color: var(--success, #15803d);
    background: color-mix(in srgb, #22c55e 12%, transparent);
    border: 1px solid color-mix(in srgb, #22c55e 35%, transparent);
  }

  .banner.error {
    color: var(--danger, #b91c1c);
    background: color-mix(in srgb, #ef4444 10%, transparent);
    border: 1px solid color-mix(in srgb, #ef4444 30%, transparent);
  }

  .edit-form {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .edit-form .field {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }

  .edit-form .field span {
    font-size: var(--text-xs, 0.75rem);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--text-muted);
    font-weight: 600;
  }

  .edit-form input,
  .edit-form textarea {
    width: 100%;
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 0.5rem 0.65rem;
    font: inherit;
    background: var(--surface);
    color: inherit;
    resize: vertical;
  }

  .edit-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.65rem;
    align-items: center;
  }

  @media (max-width: 900px) {
    .grid {
      grid-template-columns: 1fr;
    }
  }
</style>
